import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import SignGuestbookModal from './Guestbook/SignGuestbookModal';
import ShareModal from '../Modals/ShareModal';
import TributeNotificationSettings from './TributeNotificationSettings';
import ModerationQueue from '../Dashboard/ModerationQueue';
import GuestbookExportModal from '../Dashboard/GuestbookExportModal';
import { notifyMemorialFollowers, useTributeNotifications } from '../../hooks/useTributeNotifications';
import { writeFetch, getSession } from '../../api';
import { useAuth } from '../../context/AuthContext';
import { useMemorialOwner } from '../../hooks/useMemorialOwner';

const { FiEdit3, FiMessageCircle, FiHeart, FiLock, FiShare2, FiShield, FiChevronDown, FiChevronUp, FiClock, FiDownload, FiSun, FiWind, FiTrendingUp } = FiIcons;

const GIFT_CONFIGS = {
  candle:  { emoji: '🕯️', label: 'Lit a Candle',   color: 'text-amber-500', bg: 'bg-amber-50' },
  flower:  { emoji: '🌹', label: 'Left Flowers',    color: 'text-rose-500',  bg: 'bg-rose-50'  },
  dove:    { emoji: '🕊️', label: 'Sent a Dove',    color: 'text-blue-500',  bg: 'bg-blue-50'  },
  prayer:  { emoji: '🙏', label: 'Offered Prayer',  color: 'text-accent-gold', bg: 'bg-brand-light' },
};

const GiftBadge = ({ type, option }) => {
  const config = GIFT_CONFIGS[type] || GIFT_CONFIGS.candle;
  const optionLabels = {
    white: 'Pure White', gold: 'Eternal Gold', blue: 'Serene Blue',
    rose: 'Red Rose', lily: 'White Lily', sunflower: 'Sunflower',
    single: 'Single Dove', pair: 'Pair of Doves',
    peace: 'for Peace', strength: 'for Strength', gratitude: 'of Gratitude',
  };
  return (
    <div className="flex flex-col items-end gap-1">
      <div className={`flex items-center gap-2 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-wider ${config.bg} ${config.color}`}>
        <span>{config.emoji}</span> {config.label}
      </div>
      {option && (
        <span className="text-[9px] font-bold text-text-muted uppercase tracking-tighter opacity-70">
          {optionLabels[option] || option}
        </span>
      )}
    </div>
  );
};

const getTributeApprovalSetting = (memorialId) => {
  const stored = localStorage.getItem(`tribute_approval_${memorialId}`);
  if (stored !== null) return stored === '1';
  return true;
};

// ── Inline Owner Moderation Panel ────────────────────────────────────────────
const OwnerModerationPanel = ({ memorial, pendingQueue, onApprove, onReject }) => {
  const [expanded, setExpanded] = useState(true);

  if (pendingQueue.length === 0) {
    return (
      <div className="bg-green-50 border border-green-200 rounded-[28px] p-5 flex items-center gap-3">
        <div className="w-8 h-8 bg-green-100 text-green-500 rounded-xl flex items-center justify-center flex-shrink-0">
          <SafeIcon icon={FiShield} className="w-4 h-4" />
        </div>
        <div>
          <p className="text-xs font-black uppercase tracking-widest text-green-700">Moderation Queue Clear</p>
          <p className="text-[11px] text-green-600 font-light">No tributes awaiting review for this memorial.</p>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-amber-50 border-2 border-amber-200 rounded-[32px] overflow-hidden"
    >
      {/* Panel header */}
      <button
        onClick={() => setExpanded(v => !v)}
        className="w-full flex items-center justify-between p-6 hover:bg-amber-100/50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-amber-500 text-white rounded-xl flex items-center justify-center shadow-sm">
            <SafeIcon icon={FiShield} className="w-4 h-4" />
          </div>
          <div className="text-left">
            <p className="text-xs font-black uppercase tracking-widest text-amber-800">
              Tribute Approval Required
            </p>
            <p className="text-[11px] text-amber-600 font-light">
              {pendingQueue.length} tribute{pendingQueue.length > 1 ? 's' : ''} pending your review
            </p>
          </div>
          <motion.span
            key={pendingQueue.length}
            initial={{ scale: 1.4 }}
            animate={{ scale: 1 }}
            className="ml-2 bg-amber-500 text-white w-6 h-6 rounded-full text-[10px] font-black flex items-center justify-center shadow"
          >
            {pendingQueue.length}
          </motion.span>
        </div>
        <SafeIcon icon={expanded ? FiChevronUp : FiChevronDown} className="w-5 h-5 text-amber-600" />
      </button>

      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="overflow-hidden"
          >
            <div className="border-t border-amber-200 divide-y divide-amber-100">
              {pendingQueue.map(item => (
                <motion.div
                  key={item.id}
                  layout
                  exit={{ opacity: 0, x: -20 }}
                  className="p-5 flex flex-col sm:flex-row gap-4 items-start bg-white/60 hover:bg-white/90 transition-colors"
                >
                  {/* Avatar */}
                  <div className="w-10 h-10 rounded-xl bg-amber-100 text-amber-700 font-serif text-lg flex items-center justify-center flex-shrink-0 border border-amber-200">
                    {item.author?.charAt(0) || '?'}
                  </div>

                  <div className="flex-grow min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <span className="font-bold text-accent-navy text-sm">{item.author}</span>
                      {item.relation && (
                        <span className="text-[9px] font-black uppercase tracking-widest text-text-muted bg-brand-light px-2 py-0.5 rounded-md">
                          {item.relation}
                        </span>
                      )}
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-widest ${(GIFT_CONFIGS[item.tributeType] || GIFT_CONFIGS.candle).bg} ${(GIFT_CONFIGS[item.tributeType] || GIFT_CONFIGS.candle).color}`}>
                        {(GIFT_CONFIGS[item.tributeType] || GIFT_CONFIGS.candle).emoji}
                        {(GIFT_CONFIGS[item.tributeType] || GIFT_CONFIGS.candle).label}
                      </span>
                      <span className="flex items-center gap-1 text-[9px] font-bold text-amber-600 uppercase tracking-widest">
                        <SafeIcon icon={FiClock} className="w-3 h-3" /> Pending
                      </span>
                    </div>
                    <p className="text-sm text-text-muted font-light italic leading-relaxed mb-3">
                      "{item.message}"
                    </p>
                    <div className="flex gap-2">
                      <button
                        onClick={() => onApprove(item)}
                        className="px-4 py-2 bg-green-500 text-white rounded-xl text-[10px] font-black uppercase tracking-widest flex items-center gap-1.5 hover:bg-green-600 transition-colors"
                      >
                        <SafeIcon icon={FiIcons.FiCheck} className="w-3 h-3" /> Approve
                      </button>
                      <button
                        onClick={() => onReject(item)}
                        className="px-4 py-2 bg-red-50 text-red-500 rounded-xl text-[10px] font-black uppercase tracking-widest flex items-center gap-1.5 hover:bg-red-100 transition-colors border border-red-100"
                      >
                        <SafeIcon icon={FiIcons.FiX} className="w-3 h-3" /> Reject
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// ── TabTributes ──────────────────────────────────────────────────────────────
const TabTributes = ({ memorial }) => {
  const { user } = useAuth();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [shareData, setShareData] = useState(null);
  const [tributes, setTributes] = useState(memorial.tributes || []);
  const [filter, setFilter] = useState('all');
  const [pendingQueue, setPendingQueue] = useState([]);
  const { prefs, savePrefs } = useTributeNotifications(memorial.id);

  const requiresApproval = getTributeApprovalSetting(memorial.id);
  const { isOwner } = useMemorialOwner(memorial); // server-verified memorial owner

  // Listen for new pending tributes from the guestbook modal
  useEffect(() => {
    const handlePending = (e) => {
      if (e.detail.memorialId !== memorial.id) return;
      setPendingQueue(prev => [{
        id: Date.now(),
        author: e.detail.author,
        relation: e.detail.relation,
        message: e.detail.message,
        tributeType: e.detail.tributeType,
        giftOption: e.detail.giftOption,
        submittedAt: e.detail.submittedAt,
      }, ...prev]);
    };
    window.addEventListener('pending_tribute', handlePending);
    return () => window.removeEventListener('pending_tribute', handlePending);
  }, [memorial.id]);

  // Listen for tributes approved from global admin queue (cross-panel sync)
  useEffect(() => {
    const handleApproved = (e) => {
      if (e.detail.memorialId !== memorial.id) return;
      setTributes(prev => [e.detail.tribute, ...prev]);
      setPendingQueue(prev => prev.filter(p => p.id !== e.detail.tribute.id));
    };
    window.addEventListener('tribute_approved', handleApproved);
    return () => window.removeEventListener('tribute_approved', handleApproved);
  }, [memorial.id]);

  const handleApprove = async (item) => {
    const approvedTribute = {
      id: item.id,
      author: item.author,
      relation: item.relation,
      message: item.message,
      type: item.tributeType,
      giftOption: item.giftOption,
      date: item.submittedAt || new Date().toISOString(),
      isPrivate: false,
    };

    // Update DB
    writeFetch(`update/condolences/${item.dbId || item.id}`, {
      method: 'PUT',
      body: JSON.stringify({ is_approved: 1 }),
    }).catch(() => {});

    // Notify followers
    await notifyMemorialFollowers({
      memorialPageId: memorial.id,
      memorialName: memorial.name,
      actorName: item.author,
      tributeType: item.tributeType,
      giftType: item.giftOption,
    });

    window.dispatchEvent(new CustomEvent('memorial_activity', {
      detail: { type: item.tributeType, author: item.author, memorialId: memorial.id, timestamp: new Date().toISOString() },
    }));

    setTributes(prev => [approvedTribute, ...prev]);
    setPendingQueue(prev => prev.filter(p => p.id !== item.id));
  };

  const handleReject = (item) => {
    writeFetch(`update/condolences/${item.dbId || item.id}`, {
      method: 'PUT',
      body: JSON.stringify({ is_approved: -1 }),
    }).catch(() => {});
    setPendingQueue(prev => prev.filter(p => p.id !== item.id));
  };

  const handleNewTribute = async (newTribute) => {
    // Only add to live list if auto-approved
    if (!newTribute.pendingApproval) {
      setTributes(prev => [newTribute, ...prev]);
      if (!newTribute.isPrivate) {
        const authorName = newTribute.name || newTribute.author || 'Someone';
        await notifyMemorialFollowers({
          memorialPageId: memorial.id,
          memorialName: memorial.name,
          actorName: authorName,
          tributeType: newTribute.gift || newTribute.type || 'tribute',
          giftType: newTribute.giftOption,
        });
        window.dispatchEvent(new CustomEvent('memorial_activity', {
          detail: { type: 'tribute', author: authorName, memorialId: memorial.id, timestamp: new Date().toISOString() }
        }));
      }
    }
  };

  const filteredTributes = tributes.filter(t => {
    if (filter === 'all') return true;
    return t.type === filter || t.gift === filter;
  });

  const handleShareTribute = (tribute) => {
    setShareData({
      title: `Memory of ${memorial.name} shared by ${tribute.author || tribute.name}`,
      memorialId: memorial.id,
      url: `?tribute=${tribute.id}`,
      description: tribute.message,
    });
  };

  const calculateExportStats = () => {
    const totalTributes = tributes.length;
    const giftsDist = {};
    tributes.forEach(t => {
      const type = t.type || t.gift || 'message';
      giftsDist[type] = (giftsDist[type] || 0) + 1;
    });

    const gifts = [
      { label: 'Candles', count: giftsDist['candle'] || 0, icon: FiSun, color: 'bg-amber-400' },
      { label: 'Flowers', count: giftsDist['flower'] || 0, icon: FiHeart, color: 'bg-rose-400' },
      { label: 'Doves', count: giftsDist['dove'] || 0, icon: FiWind, color: 'bg-blue-400' },
      { label: 'Messages', count: giftsDist['prayer'] || giftsDist['message'] || 0, icon: FiMessageCircle, color: 'bg-accent-navy' },
    ].filter(g => g.count > 0);

    const stats = [
      { label: 'Total Tributes', value: totalTributes.toString(), sub: 'Lifetime', icon: FiMessageCircle, color: 'text-blue-500' },
      { label: 'Gift Types', value: gifts.length.toString(), sub: 'Variations', icon: FiHeart, color: 'text-rose-500' },
      { label: 'Most Popular', value: gifts.length > 0 ? gifts.reduce((prev, current) => (prev.count > current.count) ? prev : current).label : '—', sub: 'Tribute type', icon: FiTrendingUp, color: 'text-green-500' },
    ];

    return { stats, gifts };
  };

  const { stats, gifts } = calculateExportStats();

  return (
    <div className="space-y-8">
      {/* Header CTA */}
      <div className="bg-white p-8 md:p-12 rounded-[40px] shadow-sm border border-brand-dark flex flex-col md:flex-row items-center justify-between gap-8">
        <div className="text-center md:text-left">
          <h3 className="text-2xl font-serif text-accent-navy mb-2">The Guestbook</h3>
          <p className="text-text-muted font-light">
            Join {tributes.length} others in honoring {memorial.name.split(' ')[0]}'s beautiful memory.
          </p>
        </div>
        <div className="flex flex-col md:flex-row items-center gap-4 w-full md:w-auto">
          {isOwner && (
            <button 
              onClick={() => setExportOpen(true)}
              className="px-6 py-5 rounded-2xl border-2 border-brand-dark bg-brand-light hover:border-accent-gold text-accent-navy font-bold flex items-center gap-2 transition-all w-full md:w-auto justify-center"
            >
              <SafeIcon icon={FiDownload} />
              Export
            </button>
          )}
          <button onClick={() => setIsModalOpen(true)}
            className="btn-accent px-10 py-5 flex items-center gap-3 text-lg group w-full md:w-auto justify-center"
          >
            <SafeIcon icon={FiEdit3} className="group-hover:rotate-12 transition-transform" />
            Sign Guestbook
          </button>
        </div>
      </div>

      {/* ── Owner Moderation Panel (visible when approval is ON and user is owner) ── */}
      {isOwner && requiresApproval && (
        <OwnerModerationPanel
          memorial={memorial}
          pendingQueue={pendingQueue}
          onApprove={handleApprove}
          onReject={handleReject}
        />
      )}

      {user && (
        <TributeNotificationSettings memorialPageId={memorial.id} memorialName={memorial.name} />
      )}

      {/* Filter pills */}
      <div className="flex flex-wrap items-center gap-3">
        {['all', 'candle', 'flower', 'dove'].map(f => (
          <button key={f} onClick={() => setFilter(f)}
            className={`px-6 py-2.5 rounded-full text-xs font-bold transition-all border-2 uppercase tracking-widest ${filter === f ? 'bg-accent-navy border-accent-navy text-white' : 'bg-white border-brand-dark text-text-muted hover:border-accent-gold'}`}
          >
            {f === 'all' ? 'All Tributes' : `${f}s`}
          </button>
        ))}
      </div>

      {/* Tributes list */}
      <div className="grid grid-cols-1 gap-6">
        <AnimatePresence mode="popLayout">
          {filteredTributes.length > 0 ? (
            filteredTributes.map((tribute, idx) => (
              <motion.div layout initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.05 }} key={tribute.id}
                className="bg-white p-8 md:p-10 rounded-[32px] shadow-sm border border-brand-dark group hover:border-accent-gold transition-all duration-300"
              >
                <div className="flex flex-col md:flex-row gap-6">
                  <div className="flex-shrink-0">
                    <div className="w-16 h-16 rounded-2xl bg-brand-light flex items-center justify-center text-accent-navy font-serif text-2xl border border-brand-dark group-hover:bg-accent-gold/10 transition-colors">
                      {tribute.author?.charAt(0) || tribute.name?.charAt(0)}
                    </div>
                  </div>
                  <div className="flex-grow space-y-4">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                      <div>
                        <div className="flex items-center gap-3 mb-1">
                          <h4 className="text-xl font-bold text-accent-navy">{tribute.author || tribute.name}</h4>
                          {tribute.isPrivate && (
                            <div className="flex items-center gap-1.5 text-[9px] font-black uppercase tracking-widest text-text-muted bg-brand-light px-2 py-0.5 rounded-md">
                              <SafeIcon icon={FiLock} className="w-2.5 h-2.5" /> Private
                            </div>
                          )}
                        </div>
                        <p className="text-xs font-bold text-accent-gold uppercase tracking-widest">
                          {tribute.relation} • {new Date(tribute.date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                        </p>
                      </div>
                      <div className="flex items-center gap-3">
                        <GiftBadge type={tribute.type || tribute.gift} option={tribute.giftOption} />
                        {!tribute.isPrivate && (
                          <button onClick={() => handleShareTribute(tribute)}
                            className="p-2 bg-brand-light text-text-muted hover:text-accent-gold rounded-lg transition-colors"
                          >
                            <SafeIcon icon={FiShare2} className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                    <p className="text-lg text-text-main font-light leading-relaxed italic">"{tribute.message}"</p>
                    {tribute.gifUrl && (
                      <div className="w-full md:w-64 aspect-video rounded-2xl overflow-hidden border border-brand-dark shadow-sm">
                        <img src={tribute.gifUrl} alt="Animated memory" className="w-full h-full object-cover" />
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            ))
          ) : (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
              className="text-center py-20 bg-white rounded-[40px] border-2 border-dashed border-brand-dark"
            >
              <SafeIcon icon={FiMessageCircle} className="w-16 h-16 text-text-muted/20 mx-auto mb-6" />
              <h4 className="text-2xl font-serif text-accent-navy mb-2">Be the first to share</h4>
              <p className="text-text-muted max-w-sm mx-auto font-light">Your words of comfort mean the world to the family.</p>
              <button onClick={() => setIsModalOpen(true)}
                className="mt-8 text-accent-gold font-black uppercase tracking-[0.2em] text-xs hover:text-accent-navy transition-colors"
              >
                Write a Message
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <SignGuestbookModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        memorialName={memorial.name}
        memorialId={memorial.id}
        onSumbit={handleNewTribute}
      />
      <ShareModal isOpen={!!shareData} onClose={() => setShareData(null)} {...shareData} />
      
      <GuestbookExportModal 
        open={exportOpen} 
        onClose={() => setExportOpen(false)} 
        stats={stats} 
        gifts={gifts} 
        tributes={tributes} 
      />
    </div>
  );
};

export default TabTributes;